import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { ScrollText, ShieldCheck, ShieldAlert, RefreshCcw, Search, Filter } from "lucide-react";

export default function AdminAuditPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [action, setAction] = useState("");
  const [actorEmail, setActorEmail] = useState("");
  const [verify, setVerify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
      if (action) params.set("action", action);
      if (actorEmail) params.set("actor_email", actorEmail);
      const { data } = await adminApi.get(`/audit?${params}`);
      setEntries(data.entries);
      setTotal(data.total);
    } catch (e) {
      console.error("Audit list failed:", e);
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  }, [skip, action, actorEmail]);

  const runVerify = async () => {
    setVerifying(true);
    try {
      const { data } = await adminApi.get("/audit/verify");
      setVerify(data);
      toast[data.valid ? "success" : "error"](
        data.valid ? `Chaîne d'audit valide (${data.total} entrées)` : `${data.breaks.length} rupture(s) détectée(s)`
      );
    } catch (e) {
      console.error("Audit verify failed:", e);
      toast.error("Erreur de vérification");
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-audit-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">Immuable · Hash chain</p>
          <h1 className="text-3xl font-semibold text-white flex items-center gap-3"><ScrollText className="w-7 h-7 text-emerald-400" /> Journal d'audit</h1>
          <p className="text-sm text-slate-400 mt-1">{total.toLocaleString("fr-FR")} entrée(s). Chaque mutation admin y est écrite avec un hash SHA-256 chaîné.</p>
        </div>
        <button onClick={runVerify} disabled={verifying} data-testid="audit-verify-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/30 text-sm">
          <ShieldCheck className={`w-4 h-4 ${verifying ? "animate-pulse" : ""}`} /> Vérifier l'intégrité
        </button>
      </div>

      {verify && (
        <div className={`p-4 rounded-md border text-sm ${verify.valid ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-rose-500/10 border-rose-500/30 text-rose-300"}`} data-testid="audit-verify-result">
          {verify.valid ? (
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Chaîne valide — {verify.total} entrée(s) analysée(s), aucun tampering détecté. Dernière vérif : {verify.checked_at?.slice(0, 19).replace("T", " ")}</div>
          ) : (
            <div className="flex items-start gap-2"><ShieldAlert className="w-4 h-4 mt-0.5" /><div>
              <p className="font-semibold">{verify.breaks.length} rupture(s) dans la chaîne d'audit !</p>
              <ul className="mt-2 space-y-1 font-mono text-xs">{verify.breaks.slice(0, 5).map((b) => <li key={b.id || `${b.reason}-${b.found?.slice(0,8) || ""}`}>{b.id} : {b.reason}</li>)}</ul>
            </div></div>
          )}
        </div>
      )}

      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-3 text-slate-500" />
            <input value={action} onChange={(e) => setAction(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setSkip(0), load())}
              placeholder="Action (ex: admin_login_success)" data-testid="audit-filter-action"
              className="w-full bg-[#0b1220] border border-slate-700 rounded-md pl-8 pr-3 py-2 text-xs" />
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Filter className="w-3.5 h-3.5 absolute left-3 top-3 text-slate-500" />
            <input value={actorEmail} onChange={(e) => setActorEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setSkip(0), load())}
              placeholder="E-mail acteur" data-testid="audit-filter-actor"
              className="w-full bg-[#0b1220] border border-slate-700 rounded-md pl-8 pr-3 py-2 text-xs" />
          </div>
          <button onClick={() => { setSkip(0); load(); }} data-testid="audit-search-btn"
            className="px-4 py-2 rounded-md bg-[#1F5A99] text-white text-xs hover:bg-[#2b6db3]">Filtrer</button>
          <button onClick={() => { setAction(""); setActorEmail(""); setSkip(0); load(); }}
            className="p-2 rounded-md border border-slate-800 text-slate-400 hover:text-white">
            <RefreshCcw className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-auto max-h-[70vh] border border-slate-800 rounded-md">
          <table className="w-full text-xs">
            <thead className="bg-[#0b1220] sticky top-0">
              <tr className="text-left uppercase tracking-widest text-[10px] text-slate-500">
                <th className="p-3">Horodatage</th>
                <th className="p-3">Acteur</th>
                <th className="p-3">Rôle</th>
                <th className="p-3">Action</th>
                <th className="p-3">Objet</th>
                <th className="p-3">IP</th>
                <th className="p-3">Hash</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-slate-800 hover:bg-slate-800/30" data-testid={`audit-row-${e.id.slice(0,8)}`}>
                  <td className="p-3 font-mono text-slate-400">{e.created_at.slice(0, 19).replace("T", " ")}</td>
                  <td className="p-3">{e.actor_email}</td>
                  <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px] uppercase">{e.actor_role}</span></td>
                  <td className="p-3 font-mono text-emerald-400">{e.action}</td>
                  <td className="p-3 text-slate-400">{e.object_type} <span className="text-slate-600 font-mono">{e.object_id ? e.object_id.slice(0, 8) : ""}</span></td>
                  <td className="p-3 text-slate-500 font-mono">{e.ip || "—"}</td>
                  <td className="p-3 text-slate-600 font-mono">{e.hash?.slice(0, 10)}…</td>
                </tr>
              ))}
              {entries.length === 0 && !loading && (
                <tr><td colSpan={7} className="p-8 text-center text-slate-500">Aucune entrée</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4 text-xs">
          <span className="text-slate-500">Page {Math.floor(skip / limit) + 1} · Total {total.toLocaleString("fr-FR")}</span>
          <div className="flex gap-2">
            <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}
              className="px-3 py-1.5 rounded-md border border-slate-800 disabled:opacity-40 hover:bg-slate-800 text-slate-300" data-testid="audit-prev">Précédent</button>
            <button disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)}
              className="px-3 py-1.5 rounded-md border border-slate-800 disabled:opacity-40 hover:bg-slate-800 text-slate-300" data-testid="audit-next">Suivant</button>
          </div>
        </div>
      </div>
    </div>
  );
}
